import React, { useRef, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  FilterFn,
} from '@tanstack/react-table';
import { useAuth } from '../AuthContext';
import { ALL_CUSTOMERS_QUERY, CREATE_ADDRESS_MUTATION } from '../graphql/customers';
import { ALL_JOBS_QUERY, CREATE_JOB_MUTATION, ARCHIVE_JOB_MUTATION } from '../graphql/jobs';
import Table from '../components/Table';
import AddressPicker, { AddressOption, AddressPickerHandle } from '../components/Address/AddressPicker';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WorkOutlineIcon from '@mui/icons-material/AbcRounded';
import './Customers.css';

interface Job {
  id: number;
  name: string;
  description: string | null;
  status: string;
  createdAt: string;
  archivedAt: string | null;
  customerByCustomerId: { id: number; name: string };
}

interface Customer {
  id: number;
  name: string;
  archivedAt: string | null;
  addressesByCustomerId: { nodes: AddressOption[] };
}

const filterFn: FilterFn<Job> = (row, _columnId, filterValue) => {
  if (!filterValue) return true;
  const s = String(filterValue).toLowerCase();
  return (
    row.original.name.toLowerCase().includes(s) ||
    row.original.customerByCustomerId.name.toLowerCase().includes(s) ||
    (row.original.description || '').toLowerCase().includes(s)
  );
};

const columnHelper = createColumnHelper<Job>();

const buildColumns = (onArchive: (job: Job) => void, showWorkerView: boolean) => [
  columnHelper.accessor('name', {
    header: 'Job',
    cell: (info) => (
      <a href={`/jobs/${info.row.original.id}`} style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>
        {info.getValue()}
      </a>
    ),
    size: 220,
  }),
  columnHelper.accessor((row) => row.customerByCustomerId.name, {
    id: 'customer',
    header: 'Customer',
    cell: (info) => (
      <a href={`/customers/${info.row.original.customerByCustomerId.id}`} style={{ color: '#374151', textDecoration: 'none' }}>
        {info.getValue()}
      </a>
    ),
    size: 180,
  }),
  columnHelper.accessor('description', {
    header: 'Description',
    cell: (info) => <span style={{ color: '#6b7280' }}>{info.getValue() || '—'}</span>,
    size: 240,
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    size: 110,
  }),
  ...(!showWorkerView ? [
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => (
        <button
          onClick={() => onArchive(info.row.original)}
          style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}
        >
          {info.row.original.archivedAt ? 'Unarchive' : 'Archive'}
        </button>
      ),
      size: 80,
    }),
  ] : []),
];

export default function Jobs() {
  const { user } = useAuth();
  const [globalFilter, setGlobalFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ customerId: '', name: '', description: '', addressId: '' as number | '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const addressPickerRef = useRef<AddressPickerHandle>(null);

  const { loading, refetch } = useQuery(ALL_JOBS_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      const all: Job[] = data?.allJobs?.nodes || [];
      setJobs(showArchived ? all : all.filter((j) => !j.archivedAt));
    },
  });

  const { data: customersData } = useQuery(ALL_CUSTOMERS_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id,
  });

  const [createJob] = useMutation(CREATE_JOB_MUTATION);
  const [archiveJob] = useMutation(ARCHIVE_JOB_MUTATION);
  const [createAddress] = useMutation(CREATE_ADDRESS_MUTATION);

  const customers: Customer[] = (customersData?.allCustomers?.nodes || []).filter((c: Customer) => !c.archivedAt);
  const selectedCustomer = customers.find((c) => c.id === parseInt(formData.customerId, 10));
  const availableAddresses: AddressOption[] = selectedCustomer?.addressesByCustomerId.nodes || [];

  const handleArchive = async (job: Job) => {
    await archiveJob({ variables: { id: job.id, archivedAt: job.archivedAt ? null : new Date().toISOString() } });
    refetch().then((res) => {
      const all: Job[] = res.data?.allJobs?.nodes || [];
      setJobs(showArchived ? all : all.filter((j) => !j.archivedAt));
    });
  };

  const handleToggleArchived = (checked: boolean) => {
    setShowArchived(checked);
    refetch().then((res) => {
      const all: Job[] = res.data?.allJobs?.nodes || [];
      setJobs(checked ? all : all.filter((j) => !j.archivedAt));
    });
  };

  const isWorker = user?.role === 'worker';
  const columns = buildColumns(handleArchive, isWorker);

  const table = useReactTable({
    data: jobs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: filterFn,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
  });

  const handleCreate = async () => {
    if (!formData.customerId) { setFormError('Please select a customer'); return; }
    if (!formData.name.trim()) { setFormError('Job name is required'); return; }
    setSaving(true);
    setFormError(null);
    try {
      const resolvedAddressId = await addressPickerRef.current?.commit() ?? formData.addressId;
      await createJob({
        variables: {
          domainId: user!.domain_id,
          customerId: parseInt(formData.customerId, 10),
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          addressId: resolvedAddressId || null,
        },
      });
      setShowModal(false);
      setFormData({ customerId: '', name: '', description: '', addressId: '' });
      refetch().then((res) => {
        const all: Job[] = res.data?.allJobs?.nodes || [];
        setJobs(showArchived ? all : all.filter((j) => !j.archivedAt));
      });
    } catch (err: any) {
      setFormError(err.message || 'Failed to create job');
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => { setShowModal(false); setFormError(null); setFormData({ customerId: '', name: '', description: '', addressId: '' }); };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box',
  };

  return (
    <div className="customers-page">
      <div className="page-header">
        <div>
          <h1>Jobs</h1>
          <p className="subtitle">Manage jobs and their quotes</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <label style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={showArchived} onChange={(e) => handleToggleArchived(e.target.checked)} />
            Show archived
          </label>
          {!isWorker && (
            <button className="primary-button btn-icon" onClick={() => setShowModal(true)} disabled={loading}>
              <AddIcon fontSize="small" /> New Job
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        {!loading && jobs.length === 0 && !globalFilter && (
          <div className="empty-state">
            <div className="empty-icon"><WorkOutlineIcon fontSize="inherit" /></div>
            <h2>No jobs yet</h2>
            <p>Create a job for a customer to start quoting</p>
          </div>
        )}

        {(jobs.length > 0 || globalFilter) && (
          <>
            <div className="filter-bar">
              <input
                type="text"
                className="filter-input"
                placeholder="Search by job name or customer..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </div>
            <Table table={table} isLoading={loading} />
          </>
        )}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 8, width: 480, maxWidth: '90%', boxShadow: '0 20px 25px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>New Job</h2>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }} onClick={closeModal}>
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div style={{ padding: 24 }}>
              {formError && <div style={{ padding: 12, background: '#fee2e2', color: '#dc2626', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{formError}</div>}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Customer <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select style={inputStyle} value={formData.customerId} onChange={(e) => setFormData({ ...formData, customerId: e.target.value, addressId: '' })} disabled={saving}>
                  <option value="">Select a customer...</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Job Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input type="text" style={inputStyle} placeholder="e.g., Spring 2026 Install" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} disabled={saving} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Description (Optional)</label>
                <textarea style={{ ...inputStyle, minHeight: 72, resize: 'vertical' } as React.CSSProperties} placeholder="Brief description..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} disabled={saving} />
              </div>
              {selectedCustomer && (
                <div>
                  <AddressPicker
                    ref={addressPickerRef}
                    label="Job Address (Optional)"
                    addresses={availableAddresses}
                    value={formData.addressId}
                    onChange={(addressId) => setFormData({ ...formData, addressId })}
                    disabled={saving}
                    onCreateAddress={async (fields) => {
                      const result = await createAddress({
                        variables: {
                          customerId: selectedCustomer.id,
                          domainId: user!.domain_id,
                          street: fields.street,
                          city: fields.city,
                          state: fields.state,
                          zip: fields.zip,
                          addressType: 'primary',
                        },
                        refetchQueries: [{ query: ALL_CUSTOMERS_QUERY, variables: { domainId: user!.domain_id } }],
                      });
                      return result.data.createAddress.address;
                    }}
                  />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
              <button style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }} onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }} onClick={handleCreate} disabled={saving}>
                {saving ? 'Saving...' : 'Create Job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
